import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Plus, Edit, Trash2, X, Euro, Calendar, FileText } from 'lucide-react';
import { notify } from '../../utils/notifications';

const TipiAbbonamentoLinea = ({ lineaId, lineaNome }) => {
  const [tipi, setTipi] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingTipo, setEditingTipo] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    descrizione: '',
    modalita: 'SCUOLE',
    durata_giorni: '',
    importo: '',
    importo_sola_direzione: '',
    importo_settembre: '',
    attivo: true
  });

  const isScuole = formData.modalita === 'SCUOLE';

  useEffect(() => {
    caricaTipi();
  }, [lineaId]);

  const caricaTipi = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/abbonamenti/tipi-abbonamento/?linea=${lineaId}`);
      setTipi(res.data.results || res.data);
    } catch (error) {
      console.error('Errore caricamento tipi abbonamento:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Errore nel caricamento dei tipi di abbonamento';
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (tipo = null) => {
    if (tipo) {
      setEditingTipo(tipo);
      setFormData(tipo);
    } else {
      setEditingTipo(null);
      setFormData({
        linea: lineaId,
        nome: '',
        descrizione: '',
        modalita: 'SCUOLE',
        durata_giorni: '',
        importo: '',
        importo_sola_direzione: '',
        importo_settembre: '',
        attivo: true
      });
    }
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const toastId = notify.loading(editingTipo ? 'Aggiornamento...' : 'Creazione...');

    // I campi numerici lasciati in bianco vanno inviati come null: una
    // stringa vuota non è un numero e il server la rifiuta.
    const numerico = (v) => (v === '' || v === undefined ? null : v);
    const payload = {
      ...formData,
      linea: lineaId,
      durata_giorni: numerico(formData.durata_giorni),
      importo_sola_direzione: numerico(formData.importo_sola_direzione),
      importo_settembre: numerico(formData.importo_settembre),
    };

    try {
      if (editingTipo) {
        await api.put(`/abbonamenti/tipi-abbonamento/${editingTipo.id}/`, payload);
        notify.update(toastId, { type: 'success', render: 'Tipo abbonamento aggiornato!' });
      } else {
        await api.post('/abbonamenti/tipi-abbonamento/', payload);
        notify.update(toastId, { type: 'success', render: 'Tipo abbonamento creato!' });
      }
      await caricaTipi();
      setShowModal(false);
    } catch (error) {
      console.error('Errore salvataggio tipo abbonamento:', error);
      const dati = error.response?.data || {};
      // Un nome già usato sul gruppo arriva come non_field_errors
      const duplicato = dati.non_field_errors?.[0];
      const primoErrore = Object.values(dati).flat().find((v) => typeof v === 'string');
      const errorMsg = dati.detail
        || (duplicato && duplicato.includes('insieme unico')
              ? 'Esiste già un tipo di abbonamento con questo nome su questo gruppo.'
              : null)
        || primoErrore
        || 'Errore nel salvataggio del tipo abbonamento';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  const handleDelete = async (tipoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo tipo di abbonamento?')) return;

    const toastId = notify.loading('Eliminazione...');
    try {
      await api.delete(`/abbonamenti/tipi-abbonamento/${tipoId}/`);
      notify.update(toastId, { type: 'success', render: 'Tipo abbonamento eliminato!' });
      await caricaTipi();
    } catch (error) {
      console.error('Errore eliminazione tipo abbonamento:', error);
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || error.message || 'Errore nell\'eliminazione del tipo abbonamento';
      notify.update(toastId, { type: 'error', render: errorMsg });
    }
  };

  if (loading) {
    return <div className="animate-pulse">Caricamento...</div>;
  }

  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-4">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <Euro className="w-4 h-4 text-green-600" />
          Tipi di Abbonamento ({tipi.length})
        </h2>
        <button
          onClick={() => handleOpenModal()}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition"
        >
          <Plus className="w-4 h-4" />
          Nuovo Tipo
        </button>
      </div>

      {tipi.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <FileText className="w-12 h-12 mx-auto mb-2 opacity-30" />
          <p>Nessun tipo di abbonamento configurato</p>
          <p className="text-sm">Aggiungi i tipi di abbonamento disponibili per questa linea</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {tipi.map((tipo) => (
            <div
              key={tipo.id}
              className={`border rounded-lg p-4 ${
                tipo.attivo ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1">
                  <h3 className="font-bold text-lg text-gray-900">{tipo.nome}</h3>
                  {tipo.descrizione && (
                    <p className="text-sm text-sm text-gray-500 mt-0.5">{tipo.descrizione}</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleOpenModal(tipo)}
                    className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition"
                    title="Modifica"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(tipo.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition"
                    title="Elimina"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3 pt-3 border-t border-gray-200">
                <div>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Euro className="w-3 h-3" /> {tipo.modalita === 'SCUOLE' ? 'Quota mensile' : 'Prezzo'}
                  </p>
                  <p className="text-lg font-bold text-green-600">
                    €{tipo.importo}
                    {tipo.modalita === 'SCUOLE' && tipo.importo_sola_direzione && (
                      <span className="text-xs font-normal text-gray-500"> · €{tipo.importo_sola_direzione} sola direzione</span>
                    )}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 flex items-center gap-1">
                    <Calendar className="w-3 h-3" /> {tipo.modalita === 'SCUOLE' ? 'Anno scolastico' : 'Durata'}
                  </p>
                  <p className="text-lg font-bold text-blue-600">
                    {tipo.modalita === 'SCUOLE'
                      ? `€${(
                          (tipo.importo_settembre !== null && tipo.importo_settembre !== undefined
                            ? Number(tipo.importo_settembre)
                            : Number(tipo.importo) / 2) + Number(tipo.importo) * 8
                        ).toFixed(2)}`
                      : `${tipo.durata_giorni} gg`}
                  </p>
                  {tipo.modalita === 'SCUOLE' && (
                    <p className="text-[11px] text-gray-500">
                      settembre €
                      {(tipo.importo_settembre !== null && tipo.importo_settembre !== undefined
                        ? Number(tipo.importo_settembre)
                        : Number(tipo.importo) / 2
                      ).toFixed(2)}
                    </p>
                  )}
                </div>
              </div>

              {!tipo.attivo && (
                <div className="mt-3 pt-3 border-t border-gray-300">
                  <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                    Non disponibile
                  </span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal Crea/Modifica */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full">
            <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">
                {editingTipo ? 'Modifica Tipo Abbonamento' : 'Nuovo Tipo Abbonamento'}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  required
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Es: Mensile, Trimestrale, Annuale"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Descrizione
                </label>
                <textarea
                  value={formData.descrizione}
                  onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                  rows={3}
                  placeholder="Descrizione opzionale"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">
                  Modalità *
                </label>
                <select
                  required
                  value={formData.modalita}
                  onChange={(e) => setFormData({ ...formData, modalita: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                >
                  <option value="SCUOLE">Scuole — anno scolastico a rate mensili</option>
                  <option value="PERIODO">Periodo personalizzato — date libere</option>
                </select>
                <p className="text-[11px] text-gray-500 mt-1">
                  {isScuole
                    ? 'Dal 15 settembre al 30 giugno: settembre a metà quota, giugno gratuito.'
                    : 'Date di inizio e fine inserite a mano su ogni abbonamento.'}
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    {isScuole ? 'Quota mensile (€) *' : 'Importo (€) *'}
                  </label>
                  <input
                    type="number"
                    required
                    min="0"
                    step="0.01"
                    value={formData.importo}
                    onChange={(e) => setFormData({ ...formData, importo: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  />
                  {isScuole && formData.importo > 0 && (
                    <p className="text-[11px] text-gray-500 mt-1">
                      Totale anno: €{(Number(formData.importo) * 8.5).toFixed(2)}
                    </p>
                  )}
                </div>

                {isScuole ? (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Quota sola andata/ritorno (€)
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.importo_sola_direzione || ''}
                      onChange={(e) => setFormData({ ...formData, importo_sola_direzione: e.target.value })}
                      placeholder="Come andata e ritorno"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Lascia vuoto se il prezzo non cambia
                    </p>
                  </div>
                ) : (
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">
                      Durata (giorni) *
                    </label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.durata_giorni || ''}
                      onChange={(e) => setFormData({ ...formData, durata_giorni: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                    />
                  </div>
                )}
              </div>

              {isScuole && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">
                    Quota settembre (€)
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.importo_settembre || ''}
                    onChange={(e) => setFormData({ ...formData, importo_settembre: e.target.value })}
                    placeholder={formData.importo ? (Number(formData.importo) / 2).toFixed(2) : 'Metà quota'}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Mezzo mese (15-30 settembre). Proposta sui nuovi abbonamenti,
                    dove resta comunque modificabile. Se vuota si propone metà quota.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="attivo"
                  checked={formData.attivo}
                  onChange={(e) => setFormData({ ...formData, attivo: e.target.checked })}
                  className="w-4 h-4 text-[#1b94ce] rounded focus:ring-[#1b94ce]"
                />
                <label htmlFor="attivo" className="text-sm font-medium text-gray-700">
                  Disponibile per nuovi abbonamenti
                </label>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition font-semibold"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-gradient-to-r from-[#1b94ce] to-[#157bb0] rounded-lg text-white hover:opacity-90 transition font-semibold"
                >
                  {editingTipo ? 'Aggiorna' : 'Crea'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TipiAbbonamentoLinea;
